import { useEffect, useRef, useState, useCallback } from 'react';
import NetClient from './NetClient';
import { get, post, postJson, setToken } from './Request';

function getCurrentTime() {
  const zeroFill = (i) => i >= 0 && i <= 9 ? "0" + i : i;

  const date = new Date();//当前时间
  const month = zeroFill(date.getMonth() + 1);//月
  const day = zeroFill(date.getDate());//日
  const hour = zeroFill(date.getHours());//时
  const minute = zeroFill(date.getMinutes());//分
  const second = zeroFill(date.getSeconds());//秒

  //当前时间
  return date.getFullYear() + "-" + month + "-" + day
    + " " + hour + ":" + minute + ":" + second;
}

export function useChatClient({ orderId, token, serverUrl, onMessage, onHeartbeat }) {
  const clientRef = useRef(null);
  if (!clientRef.current) {
    clientRef.current = new NetClient(serverUrl + '/topic', token);
  }

  // 消息重发记录
  const [pendingMessages, setPendingMessages] = useState(new Map());
  const pendingMessagesRef = useRef(new Map());

  // 同步 pending messages 到 ref，解决闭包问题
  useEffect(() => {
    pendingMessagesRef.current = pendingMessages;
  }, [pendingMessages]);


  useEffect(() => {
    setToken(token)

    const client = clientRef.current
    client.connect()
      .then(() => {
        client.subscribe(`/talk/${orderId}`, (data) => {
          recvMessage(data)
        })
        client.subscribe('/heartbeat', () => onHeartbeat())
      })
      .catch((error) => {
        console.error('连接失败:', error);
      });

    return () => client.disconnect();
  }, []);

  // 处理消息ACK
  const handleAck = useCallback((randomNo) => {
    setPendingMessages(prev => {
      console.info(`消息确认 ${randomNo}`)
      const newMap = new Map(prev);
      const pending = newMap.get(randomNo);
      if (pending) {
        // 清除重发定时器
        if (pending.retryTimer) {
          clearTimeout(pending.retryTimer);
        }
        newMap.delete(randomNo);
      }

      return newMap;
    });
  }, []);

  // WS 收到消息
  const recvMessage = (data) => {
    if (data.indexOf("05ACK") === 0) {
      const info = JSON.parse(data.replace("05ACK", ""))
      handleAck(info.randomNo)
    } else if (data.indexOf("17ChatMessageInfo") === 0) {
      const message = JSON.parse(data.replace("17ChatMessageInfo", ""));
      onMessage({
        id: message.id,
        message: message.content,
        sentTime: message.createTime,
        sender: message.creater,
        direction: message.creater === orderId ? "outgoing" : "incoming",
        type: parseMessageType(message.type),
        serial: message.serial,
      })

      // 给服务器发送 ack
      clientRef.current.send('/ack', { id: message.id, token });
    }
  }

  // 发送消息
  const sendMessage = useCallback((content, type, check) => {
    if (!content.trim()) {
      return
    }

    let messageType = 'TYPE_TEXT'
    if (type === 'image') {
      messageType = 'TYPE_IMAGE'
    }
    const randomNo = Math.floor(Math.random() * 1000000)
    const body = { content, type: messageType, randomNo, check }
    console.info(`发送消息`, body)
    if (type === 'image') {
      // 发送图片会自动断开ws
      postJson('/otc/chatMessage', body)
    } else {
      clientRef.current.send('/chatMessage', body);
    }

    // 记录消息
    pend(body)

    const message = {
      message: content,
      sentTime: getCurrentTime(),
      sender: orderId,
      direction: 'outgoing',
      type: type,
      randomNo: body.randomNo,
    }
    return message
  }, []);

  // 设置重发定时器
  const pend = (body) => {
    const retryTimer = setTimeout(() => {
      const currentPending = pendingMessagesRef.current.get(body.randomNo);
      if (currentPending) {
        console.info(`http重新尝试发送消息`, body)
        postJson('/otc/chatMessage', body)
        setPendingMessages(prev => {
          const newMap = new Map(prev);
          newMap.delete(body.randomNo);
          return newMap;
        });
      }
    }, 5000); // 5秒超时

    // 更新待发送消息列表
    setPendingMessages(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(body.randomNo);

      // 清除旧的定时器
      if (existing?.retryTimer) {
        clearTimeout(existing.retryTimer);
      }

      newMap.set(body.randomNo, {
        body,
        retryTimer,
      });

      return newMap;
    });

  }

  const messages = async () => {
    return await get('/otc/chatMessage')
      .then(data => {
        const all = data.map(v => {
          let type = parseMessageType(v.type)
          return {
            id: v.id,
            message: v.content,
            sentTime: v.createTime,
            sender: v.creater,
            direction: v.creater === orderId ? "outgoing" : "incoming",
            type: type,
            serial: v.serial,
          }
        })
        all.sort((a, b) => a.serial - b.serial);
        return all
      })
  }

  const start = async () => {
    return await post('/otc/start')
  }

  const status = async (count) => {
    return await get('/otc/status', { count })
  }

  const paid = async () => {
    return await post('/otc/giveMoney')
  }
  const image = async (id) => {
    return await fetch(`${serverUrl}/otc/image/${id}`, {
      headers: {
        Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8',
        'Content-Type': 'text',
      },
    }).then(v => v.text())
      .catch((err) => {
        return {
          success: false,
          message: err.message
        }
      });
  }

  const report = async (message) => {
    await post('/otc/error', { message: message })
  }

  const fingerprint = async (fingerprint) => {
    await post('/otc/footprint', { footprint: fingerprint })
  }

  return {
    client: clientRef.current,
    start,
    messages,
    sendMessage,
    status,
    paid,
    image,
    report,
    fingerprint,
  };
}

const parseMessageType = (source) => {
  let type = 'text';
  if (source === 'TYPE_BANKCARD') {
    type = 'custom'
  } else if (source === 'TYPE_IMAGE') {
    type = 'image'
  }
  return type
}